import React, {useState} from 'react';
import {Alert, Grid, Stack} from "@mui/material";
import TemplateEditorStandard from "./TemplateEditorStandard"
import {TemplateType} from "../../api/enum/TemplateType"
import {
    AnyTemplateJson,
    AnyTemplateParts,
    TemplateChatParts,
    TemplateStandardParts,
} from "../../api/json/TemplateJson"
import TemplateEditorChat from "./TemplateEditorChat"
import {ProjectJson} from "../../api/json/ProjectJson"
import ApiError from "../../api/ApiError"
import useSaveShortcut from "../../hooks/useSaveShortcut"
import AsyncButton from "../button/AsyncButton"
import TemplateRenderer from "./TemplateRenderer"
import RowJson from "../../api/json/RowJson"
import TemplateUtil from "../../library/TemplateUtil"
import {TemplateRequests} from "../../api/requests/TemplateRequests"
import ErrorMessage from "../error/ErrorMessage"
import RandomRowLoader from "../rows/RandomRowLoader"
import {ProjectType} from "../../api/enum/ProjectType"
import ClassifierTemplateUtil from "../../library/ClassifierTemplateUtil"

interface TemplateEditorProps {
    project: ProjectJson
    readonly?: boolean
    template?: AnyTemplateJson
    didSave: (template: AnyTemplateJson) => void
}

function TemplateEditor({project, readonly, template, didSave}: TemplateEditorProps) {
    const type: TemplateType = template?.type ?? TemplateType.CHAT

    const [savedParts, setSavedParts] = useState<AnyTemplateParts | undefined>(template)

    const [chatEditingParts, setChatEditingParts] = useState<TemplateChatParts>(template && template.type === TemplateType.CHAT ? template : {
        type: TemplateType.CHAT,
        system: "",
        user: "",
        assistant: "",
    })

    const [standardEditingParts, setStandardEditingParts] = useState<TemplateStandardParts>(template && template.type === TemplateType.STANDARD ? template : {
        type: TemplateType.STANDARD,
        prompt: "",
        completion: "",
        separator: "###",
        stopSequence: "\n\n###\n\n",
    })

    const [row, setExample] = useState<RowJson>()

    const [saving, setSaving] = useState(false)
    const [saveError, setSaveError] = useState<ApiError>()

    const resolveEditingParts = (): AnyTemplateParts => {
        return type === TemplateType.CHAT ? chatEditingParts : standardEditingParts
    }

    const outputPart = type === TemplateType.CHAT ? chatEditingParts.assistant : standardEditingParts.completion
    const classifierOutputError = project.type === ProjectType.CLASSIFIER
        ? ClassifierTemplateUtil.validateOutput(outputPart, project.columns)
        : null

    const handleSave = () => {
        if (classifierOutputError) return
        if (!savedParts) {
            setSaving(true)
            TemplateRequests.create(project.id, null, resolveEditingParts())
                .then(finishHandleSave)
                .catch(setSaveError)
                .finally(() => setSaving(false))
        } else {
            if (readonly || !template) return

            let updatedParts = resolveEditingParts()

            setSaving(true)
            TemplateRequests.update(project.id, template.id, null, updatedParts)
                .then(finishHandleSave)
                .catch(setSaveError)
                .finally(() => setSaving(false))
        }
    }
    useSaveShortcut(() => handleSave())

    const finishHandleSave = (saved: AnyTemplateJson) => {
        setSavedParts(saved)
        if (saved.type === TemplateType.CHAT) {
            setChatEditingParts(saved)
        } else {
            setStandardEditingParts(saved)
        }
        didSave(saved)
    }

    const extraneousFields = template ? TemplateUtil.findAllReferences(template)
        .filter(reference => project.columns.map(column => column.reference)
            .indexOf(reference) === -1) : null

    let unsavedChanges = false
    if (savedParts) {
        if (type === TemplateType.CHAT && savedParts.type === TemplateType.CHAT) {
            if (chatEditingParts.system !== savedParts.system
                || chatEditingParts.user !== savedParts.user
                || chatEditingParts.assistant !== savedParts.assistant
            ) {
                unsavedChanges = true
            }
        } else if (type === TemplateType.STANDARD && savedParts.type === TemplateType.STANDARD) {
            if (standardEditingParts.prompt !== savedParts.prompt
                || standardEditingParts.completion !== savedParts.completion
                || standardEditingParts.separator !== savedParts.separator
                || standardEditingParts.stopSequence !== savedParts.stopSequence
            ) {
                unsavedChanges = true
            }
        }
    } else {
        unsavedChanges = true
    }

    return (
        <>
            {type === TemplateType.STANDARD && (
                <TemplateEditorStandard columns={project.columns}
                                        templateParts={standardEditingParts}
                                        readonly={readonly}
                                        classifierOutput={project.type === ProjectType.CLASSIFIER}
                                        onChange={setStandardEditingParts}/>
            )}
            {type === TemplateType.CHAT && (
                <TemplateEditorChat columns={project.columns}
                                    templateParts={chatEditingParts}
                                    readonly={readonly}
                                    classifierOutput={project.type === ProjectType.CLASSIFIER}
                                    onChange={setChatEditingParts}/>
            )}
            {!readonly &&
                <AsyncButton waiting={saving}
                             sx={{mt: 2}}
                             disabled={!unsavedChanges || !!classifierOutputError}
                             onClick={() => handleSave()}>Save</AsyncButton>
            }

            <Stack spacing={1} sx={{mt: 2}}>
                {!readonly && classifierOutputError &&
                    <Alert severity="error">{classifierOutputError}</Alert>}
                {extraneousFields && extraneousFields.length > 0 &&
                    <Alert severity="warning">Extraneous references found: {extraneousFields.join(", ")}</Alert>}
                {saveError && <ErrorMessage error={saveError}/>}
            </Stack>

            <Grid item xs={12} lg={6} sx={{display: {xs: "none", sm: "block"}}}>
                <RandomRowLoader project={project} row={row} handleExample={setExample}>
                    {row &&
                        <TemplateRenderer templateParts={resolveEditingParts()}
                                          dataSource={row.cells}
                                          staticSystemPromptTreatment="show"
                        />
                    }
                </RandomRowLoader>
            </Grid>
        </>
    )
}

export default TemplateEditor
